import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../auth/AuthContext";
import { useI18n } from "../i18n/I18nContext";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { Check, Lock, Sparkles, CreditCard, Upload, ArrowRight, Clock, Award, Gem, Link2, X } from "lucide-react";
import usePageMeta from "../lib/usePageMeta";

const STRIPE_LINK = "https://buy.stripe.com/dRmeVeeXU1A895U4g24F202";

// Manual-payment instructions are pulled from /app/frontend/src/i18n/locales.json
// under `payInfo.regions` — single source of truth shared by /payment-info,
// the Services modal, and the backend SendGrid email.
const METHOD_REGION_MAP = {
  zelle: ["zelle"],
  interac: ["interac"],
  transfer: ["hayti", "dr", "chile"],
  paypal: ["paypal"],
};

export default function Services() {
  const { user } = useAuth();
  const { t, lang } = useI18n();
  usePageMeta({ title: t("meta.servicesTitle"), description: t("meta.servicesDesc"), path: "/services" });
  const nav = useNavigate();
  const [services, setServices] = useState({});
  const [modal, setModal] = useState(null); // { package }

  useEffect(() => {
    api.get("/services").then((r) => setServices(r.data || {})).catch(() => setServices({}));
  }, []);

  const isMember = user && (user.role === "member" || user.role === "admin");

  const onBuy = (pkg) => {
    if (!user) {
      nav(`/login?next=${encodeURIComponent("/services")}`);
      return;
    }
    if (!isMember) return;
    setModal({ package: pkg });
  };

  const whyFeatures = t("services.why.features") || [];
  const whyIcons = [Clock, Award, Gem, Link2];

  return (
    <div data-testid="services-page" className="bg-[#050505] min-h-screen text-white">
      <Header />

      {/* Hero */}
      <section className="border-b border-[#141414] relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#e6192b]/5 via-black to-black" />
        <div className="relative max-w-6xl mx-auto px-5 md:px-10 py-20 md:py-28">
          <div className="inline-flex items-center gap-2 border border-[#d4af37]/40 bg-[#d4af37]/5 px-3 py-1.5 mb-5" data-testid="member-badge">
            <Lock size={12} className="text-[#d4af37]" />
            <span className="overline text-[10px] text-[#d4af37]">{t("services.overline")}</span>
          </div>
          <h1 className="font-serif text-4xl md:text-6xl tracking-tight leading-[1.05] max-w-4xl">
            {t("services.title")}
          </h1>
          <p className="mt-3 font-condensed tracking-[0.18em] uppercase text-[12px] text-[#d4af37]" data-testid="services-tagline">
            {t("hero.services_title")}
          </p>
          <p className="mt-5 text-neutral-300 max-w-3xl text-base md:text-lg leading-relaxed">
            {t("services.subtitle")}
          </p>
        </div>
      </section>

      {/* Pricing */}
      <section className="py-20 md:py-28 border-b border-[#141414]">
        <div className="max-w-6xl mx-auto px-5 md:px-10">
          {!user && (
            <div className="card-imperial p-6 mb-10 flex flex-col md:flex-row items-center justify-between gap-4 border-[#d4af37]/40" data-testid="services-login-prompt">
              <div>
                <div className="overline mb-1">{t("services.loginRequired")}</div>
                <div className="font-serif text-xl">{t("services.loginCtaLine")}</div>
              </div>
              <Link to="/login?next=/services" className="btn-imperial">{t("services.loginCta")}</Link>
            </div>
          )}
          {user && !isMember && (
            <div className="card-imperial p-8 mb-10 border-[#d4af37]/40" data-testid="not-member-prompt">
              <div className="flex items-start gap-4">
                <div className="h-12 w-12 bg-[#d4af37]/10 border border-[#d4af37]/40 flex items-center justify-center">
                  <Lock size={20} className="text-[#d4af37]" />
                </div>
                <div className="flex-1">
                  <div className="overline">{t("services.notMember.overline")}</div>
                  <div className="font-serif text-2xl mt-1">{t("services.notMember.title")}</div>
                  <p className="mt-3 text-neutral-400 text-sm max-w-2xl">
                    {t("services.notMember.body")}
                  </p>
                  <div className="mt-5 flex flex-wrap gap-3">
                    <a
                      href="https://wa.me/15617187259?text=Hello%2C%20I%20want%20to%20become%20a%20LeadmotivH%20HAYTI%20member."
                      target="_blank"
                      rel="noreferrer"
                      className="btn-imperial"
                      data-testid="become-member-whatsapp"
                    >
                      {t("services.notMember.becomeMember")} <ArrowRight size={14} />
                    </a>
                    <Link to="/payment-info" className="btn-outline-gold">{t("services.notMember.seePayments")}</Link>
                  </div>
                </div>
              </div>
            </div>
          )}

          <div className="grid md:grid-cols-3 gap-5" data-testid="services-grid">
            {["intro", "professional", "expert"].map((key) => {
              const s = services[key];
              if (!s) return null;
              const highlight = key === "professional";
              return (
                <div
                  key={key}
                  className={`relative card-imperial p-7 md:p-8 flex flex-col ${highlight ? "gold-shimmer-border border-[#e6192b]/50" : ""} ${key === "expert" ? "border-[#e6192b]/40" : ""}`}
                  data-testid={`service-card-${key}`}
                >
                  {highlight && (
                    <div className="absolute -top-3 left-6 bg-[#e6192b] text-white px-3 py-1 text-[10px] font-condensed tracking-widest uppercase inline-flex items-center gap-1">
                      <Sparkles size={11} /> {t("services.recommend")}
                    </div>
                  )}
                  <div className="overline mb-3">{t(`services.${key}.title`) || s.label}</div>
                  <div className="flex items-baseline gap-2">
                    <span className="font-serif text-5xl md:text-6xl">${s.amount}</span>
                    <span className="text-neutral-500 text-sm">USD</span>
                  </div>
                  {s.description && (
                    <div className="font-serif text-base text-neutral-300 mt-3 italic">{s.description}</div>
                  )}
                  {s.audience && (
                    <div className="mt-4 border-l-2 border-[#d4af37]/50 pl-3">
                      <div className="overline text-[10px]">{t("services.audience")}</div>
                      <div className="text-xs text-neutral-400 mt-1">{s.audience}</div>
                    </div>
                  )}
                  <div className="overline mt-5 mb-3">{t("services.includes")}</div>
                  <ul className="space-y-2.5 text-sm text-neutral-300 flex-1">
                    {s.features.map((f, i) => (
                      <li key={i} className="flex items-start gap-2">
                        <Check size={14} className={`mt-0.5 flex-shrink-0 ${key === "expert" ? "text-[#e6192b]" : "text-[#d4af37]"}`} />
                        <span>{f}</span>
                      </li>
                    ))}
                  </ul>
                  <button
                    onClick={() => onBuy(key)}
                    disabled={user && !isMember}
                    className={`mt-8 w-full ${highlight ? "btn-imperial" : "btn-outline-gold"} ${user && !isMember ? "opacity-40 cursor-not-allowed" : ""}`}
                    data-testid={`service-buy-${key}`}
                  >
                    {user && !isMember ? (
                      <><Lock size={13} /> {t("services.membersOnly")}</>
                    ) : (
                      <>{t("services.cta.buy") || t("services.buy")} <ArrowRight size={14} /></>
                    )}
                  </button>
                </div>
              );
            })}
          </div>

          {/* Comment Payer */}
          <div className="mt-16 card-imperial p-7 md:p-9" data-testid="how-to-pay">
            <div className="overline mb-2">{t("services.pay.overline")}</div>
            <h3 className="font-serif text-2xl md:text-3xl mb-6">{t("services.pay.title")}</h3>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {(t("services.pay.items") || []).map((it, i) => (
                <div key={i} className="border border-[#1a1a1a] p-4" data-testid={`pay-item-${i}`}>
                  <div className="font-condensed text-xs tracking-widest uppercase text-[#d4af37]">{it.label}</div>
                  <div className="text-sm text-neutral-300 mt-1.5">{it.desc}</div>
                </div>
              ))}
            </div>
            <div className="mt-5 text-xs text-neutral-500 italic">{t("services.pay.notice")}</div>
          </div>

          {/* Comparison table */}
          <div className="mt-16" data-testid="compare-section">
            <div className="overline mb-2">{t("services.compare.overline")}</div>
            <h3 className="font-serif text-2xl md:text-3xl mb-4">{t("services.compare.title")}</h3>
            <div className="flex flex-wrap gap-2 mb-6" data-testid="feature-badges">
              {[
                { k: "services.feature_payment", icon: CreditCard },
                { k: "services.feature_leads", icon: Sparkles },
                { k: "services.feature_brand", icon: Gem },
                { k: "services.feature_optimize", icon: Award },
              ].map(({ k, icon: Ic }) => {
                const label = t(k, "");
                if (!label || label === k) return null;
                return (
                  <span
                    key={k}
                    data-testid={`feat-${k.split(".").pop()}`}
                    className="inline-flex items-center gap-2 border border-[#d4af37]/40 bg-[#d4af37]/5 px-3 py-1.5 text-[11px] font-condensed tracking-widest uppercase text-[#d4af37]"
                  >
                    <Ic size={12} /> {label}
                  </span>
                );
              })}
            </div>
            <div className="card-imperial overflow-x-auto">
              <table className="w-full text-sm min-w-[560px]">
                <thead className="bg-[#0a0a0a] border-b border-[#1a1a1a]">
                  <tr>
                    <th className="text-left px-4 py-3 overline">Feature</th>
                    <th className="text-left px-4 py-3 overline text-neutral-300">Intro</th>
                    <th className="text-left px-4 py-3 overline text-[#d4af37]">Professionnel</th>
                    <th className="text-left px-4 py-3 overline text-[#e6192b] bg-[#e6192b]/5">Expert</th>
                  </tr>
                </thead>
                <tbody>
                  {(t("services.compare.rows") || []).map((r, i) => (
                    <tr key={i} className="border-b border-[#141414] last:border-0" data-testid={`compare-row-${i}`}>
                      <td className="px-4 py-3 text-neutral-400 font-condensed tracking-wide">{r.label}</td>
                      {r.values.map((v, j) => (
                        <td
                          key={j}
                          className={`px-4 py-3 ${j === 2 ? "bg-[#e6192b]/5 text-white font-medium" : j === 1 ? "text-[#d4af37]/90" : "text-neutral-300"}`}
                        >
                          {v === "yes" ? <span className={j === 2 ? "text-[#e6192b]" : "text-[#d4af37]"}>✓</span>
                            : v === "yes-opt" ? <span className="text-[#e6192b]">✓ Optimized</span>
                            : v === "no" ? <span className="text-neutral-600">—</span>
                            : v}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </section>

      {/* Why us */}
      <section className="py-20 md:py-28">
        <div className="max-w-6xl mx-auto px-5 md:px-10">
          <div className="max-w-2xl mb-12">
            <div className="overline mb-3">{t("services.why.overline")}</div>
            <h2 className="font-serif text-4xl md:text-5xl tracking-tight">{t("services.why.title")}</h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {whyFeatures.map((f, i) => {
              const Icon = whyIcons[i] || Clock;
              return (
                <div key={i} className="card-imperial p-6" data-testid={`why-feature-${i + 1}`}>
                  <div className="h-11 w-11 bg-[#e6192b]/10 border border-[#e6192b]/30 flex items-center justify-center mb-4">
                    <Icon size={18} className="text-[#e6192b]" />
                  </div>
                  <div className="font-serif text-xl">{f.title}</div>
                  <div className="text-sm text-neutral-400 mt-2 leading-relaxed">{f.desc}</div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <Footer />

      {modal && (
        <ServiceCheckoutModal
          pkg={modal.package}
          service={services[modal.package]}
          onClose={() => setModal(null)}
          onPaid={(orderId) => { setModal(null); nav(`/services/success?order_id=${orderId}`); }}
        />
      )}
    </div>
  );
}

function ServiceCheckoutModal({ pkg, service, onClose, onPaid }) {
  const { t } = useI18n();
  const [method, setMethod] = useState("stripe");
  const [region, setRegion] = useState("global");
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const submitStripe = async () => {
    setBusy(true); setErr("");
    try {
      const origin = window.location.origin;
      const { data } = await api.post("/services/checkout/session", { package: pkg, origin_url: origin });
      window.location.href = data.url || STRIPE_LINK;
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || e.message);
      setBusy(false);
    }
  };

  const submitManual = async () => {
    if (!file) { setErr(t("services.modal.errProof")); return; }
    setBusy(true); setErr("");
    try {
      const fd = new FormData();
      fd.append("package", pkg);
      fd.append("method", method);
      fd.append("region", region);
      fd.append("file", file);
      const { data } = await api.post("/services/manual", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onPaid(data.id);
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/85 z-50 flex items-center justify-center p-4" onClick={onClose} data-testid="service-checkout-modal">
      <div className="bg-[#0a0a0a] border border-[#d4af37]/40 max-w-2xl w-full max-h-[90vh] overflow-auto" onClick={(e) => e.stopPropagation()}>
        <div className="p-6 md:p-8 border-b border-[#161616] flex items-center justify-between">
          <div>
            <div className="overline">{t("services.modal.package")}</div>
            <div className="font-serif text-2xl">{service?.label} · ${service?.amount} USD</div>
          </div>
          <button onClick={onClose} className="text-neutral-500 hover:text-white" data-testid="service-modal-close">
            <X size={20} />
          </button>
        </div>
        <div className="p-6 md:p-8 space-y-6">
          <div>
            <div className="overline mb-3">{t("services.modal.method")}</div>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-2.5">
              {[
                { v: "stripe", l: "Stripe · Card" },
                { v: "zelle", l: "Zelle · USA" },
                { v: "interac", l: "Interac · CA" },
                { v: "transfer", l: "Bank · HT/DR/CL" },
                { v: "paypal", l: "PayPal" },
              ].map((o) => (
                <button
                  key={o.v}
                  data-testid={`svc-method-${o.v}`}
                  onClick={() => setMethod(o.v)}
                  className={`px-3 py-3 text-xs font-condensed tracking-widest uppercase border transition-colors ${
                    method === o.v ? "border-[#d4af37] bg-[#d4af37]/5 text-white" : "border-[#222] text-neutral-400 hover:border-[#444]"
                  }`}
                >{o.l}</button>
              ))}
            </div>
          </div>

          {method === "stripe" ? (
            <>
              <div className="text-sm text-neutral-400">{t("services.modal.stripeInfo")}</div>
              {err && <div className="text-sm text-[#e6192b]" data-testid="service-modal-error">{err}</div>}
              <button onClick={submitStripe} disabled={busy} className="btn-imperial w-full" data-testid="svc-pay-stripe">
                <CreditCard size={16} /> {busy ? t("services.modal.loading") : `${t("services.modal.pay")} $${service?.amount}`}
              </button>
              <a
                href={STRIPE_LINK}
                target="_blank"
                rel="noreferrer"
                className="block text-center text-xs text-[#d4af37] hover:underline"
                data-testid="svc-stripe-fallback-link"
              >
                {t("services.modal.altLink")}
              </a>
            </>
          ) : (
            <>
              <div className="border border-[#d4af37]/30 bg-[#d4af37]/5 p-4 space-y-4" data-testid={`manual-block-${method}`}>
                <div className="overline">{method.toUpperCase()}</div>
                {(METHOD_REGION_MAP[method] || []).map((rk) => {
                  const region = (t("payInfo.regions") || []).find((r) => r.key === rk);
                  if (!region) return null;
                  return (
                    <div key={rk} className="text-sm" data-testid={`manual-region-${rk}`}>
                      <div className="font-condensed tracking-widest uppercase text-[11px] text-[#d4af37]">{region.region}</div>
                      <div className="font-serif text-base text-white mt-0.5">{region.recipient}</div>
                      <ul className="mt-2 space-y-1">
                        {region.lines.map((l, i) => (
                          <li key={i} className="flex items-baseline gap-2 text-neutral-300">
                            <span className="text-[11px] text-neutral-500 min-w-[140px]">{l.label}</span>
                            <span className="font-condensed tracking-wide">{l.value}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  );
                })}
              </div>
              <label
                htmlFor="svc-file"
                className="border border-dashed border-[#333] hover:border-[#d4af37] cursor-pointer p-6 flex flex-col items-center text-neutral-400 transition-colors"
                data-testid="svc-file-drop"
              >
                <Upload size={18} className="mb-2 text-[#d4af37]" />
                <span className="text-sm">{file ? file.name : t("services.modal.uploadProof")}</span>
              </label>
              <input id="svc-file" type="file" accept="image/*,.pdf" className="hidden" onChange={(e) => setFile(e.target.files?.[0])} data-testid="svc-file-input" />
              {err && <div className="text-sm text-[#e6192b]">{err}</div>}
              <button onClick={submitManual} disabled={busy} className="btn-imperial w-full" data-testid="svc-submit-manual">
                {busy ? t("services.modal.sending") : t("services.modal.submit")}
              </button>
              <div className="text-xs text-neutral-500">{t("services.modal.afterApproval")}</div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
